import {
    get,
    set,
    has,
    omitBy,
    isNil,
    isEmpty,
    countBy,
    defaults,
    cloneDeep,
} from 'lodash';

import { errors } from '@paychex/core';
import { Fetch, Message } from '@paychex/core/types/data';

export type MessageStatus = 'FATAL' | 'ERROR' | 'NONE';
export type RemoteStatus = 'CRITICAL' | 'CRITICAL_ERROR' | 'WARNING' | 'ERROR' | 'NOTIFICATION' | 'INFORMATION';

/**
 * Represents a message returned by a Paychex Remote.
 */
export interface RemoteMessage {
    messageId: string,
    status: RemoteStatus,
}

const { error, fatal, ignore, ERROR, FATAL } = errors;

const STATUS: Record<RemoteStatus, MessageStatus> = {
    CRITICAL: 'FATAL',
    CRITICAL_ERROR: 'FATAL',
    WARNING: 'ERROR',
    ERROR: 'ERROR',
    NOTIFICATION: 'NONE',
    INFORMATION: 'NONE',
};

function withEnsureRemote(fetch: Fetch): Fetch {
    return async function isRemoteOperation(request, ...args) {
        if (!has(request, 'operation') || !has(request, 'destination'))
            throw error('Request is not a valid Remote operation.', fatal(request));
        if (request.method !== 'POST')
            throw error('Remote operations should use POST.', fatal(request));
        return fetch(request, ...args);
    } as Fetch;
}

function withRemoteEnvelope(fetch: Fetch): Fetch {
    return async function useRemoteEnvelope(request, ...args) {
        const clone = cloneDeep(request);
        clone.body = {
            params: [clone.body == null ? null : clone.body],
            operation: clone.operation,
            destination: clone.destination,
            ns: 'com.paychex.framework.remoting.dto.RemoteObjectRequest',
        };
        return fetch(clone, ...args);
    } as Fetch;
}

function asSeverity(status: RemoteStatus): MessageStatus {
    return STATUS[status];
}

function asMessage(message: RemoteMessage): Message {
    return {
        code: message.messageId,
        severity: asSeverity(message.status),
        data: [ omitBy(message, isNil) ]
    };
}

function withUnwrapRemoteMessages(fetch: Fetch): Fetch {
    return async function unwrapRemoteMessages(request, ...args) {
        const response = await fetch(request, ...args);
        const messages = get(response, 'data.messages', []).map(asMessage);
        if (!isEmpty(messages)) {
            set(response, 'meta.error', true);
            set(response, 'meta.messages', messages);
            const data = { response };
            const severities = countBy(messages, 'severity');
            if (severities[FATAL]) {
                throw error('Remote operation contained critical errors.', fatal(data));
            } else if (severities[ERROR]) {
                throw error('Remote operation contained errors or warnings.', data);
            } else {
                throw error('Remote operation contained notifications.', ignore(data));
            }
        }
        return response;
    } as Fetch;
}

function withUnwrapResponseEnvelope(fetch: Fetch): Fetch {
    return async function unwrapResponseEnvelope(request, ...args) {
        const response = await fetch(request, ...args);
        const clone = cloneDeep(response);
        clone.data = get(clone, 'data.data', clone.data);
        return clone;
    } as Fetch;
}

function withRemoteTraceability(fetch: Fetch): Fetch {
    return async function useRemoteTraceability(request, ...args) {
        const clone = cloneDeep(request, ...args);
        defaults(clone.headers, {
            'x-payx-svc': request.destination
        });
        return fetch(clone);
    } as Fetch;
}

/**
 * Wraps the data pipeline to provide functionality specific to Paychex Remote endpoints, including:
 *
 * - defaulting traceability headers based on Remote operation data
 * - unwrapping RemoteObjectResponses to handle errors and content
 * - wrapping data payloads in RemoteObjectRequest envelopes
 *
 * @param fetch The fetch request to wrap with Paychex Remote functionality.
 * @returns A fetch method that handles Paychex Remote endpoints.
 */
export function withRemote(fetch: Fetch): Fetch {

    let remote = fetch;

    remote = withRemoteTraceability(remote);
    remote = withUnwrapRemoteMessages(remote);
    remote = withUnwrapResponseEnvelope(remote);
    remote = withRemoteEnvelope(remote);
    remote = withEnsureRemote(remote);

    return remote;

}
